/*
 * Copyright (c) 2024 Via Transportation, Inc. All rights reserved.
 *
 * This software is distributed under license and may not be used, copied,
 * modified, or distributed without the express written permission of
 * Via Transportation, Inc.
 */

import {
    ViaButton,
    ViaSelect,
    ViaTextfield,
    ViaTypography,
    viaColorTokens,
} from "via-react-design-system";
import { useMemo, useState } from "react";
import { countries } from "country-data";
import { FormattedMessage, useIntl } from "react-intl";

import { useCommonState } from "common/hooks";

import { messages } from "../../messages";
import { useClasses } from "./styles";

const PHONE_MIN = 7;
const PHONE_MAX = 20;

interface PhoneNumberProps {
    isLoading: boolean;
    onPhoneNumberSubmit: (code: string, phone: string) => void;
}

export const PhoneNumber = ({ isLoading, onPhoneNumberSubmit }: PhoneNumberProps) => {
    const classes = useClasses();
    const { configuration } = useCommonState();
    const intl = useIntl();

    const initCountry =
        countries.all.find(({ alpha2 }) => alpha2 === configuration.clientRegion)?.alpha2 ||
        countries.US.alpha2;

    const countriesOptions = useMemo(() => {
        return [...countries.all]
            .sort((a, b) => a.name.localeCompare(b.name))
            .filter(({ countryCallingCodes }) => countryCallingCodes.length)
            .map(({ alpha2, countryCallingCodes, name }) => ({
                value: alpha2,
                label: `${name} ${countryCallingCodes[0]}`,
            }));
    }, []);

    const [countryCode, setCountryCode] = useState(initCountry);
    const [phoneNumber, setPhoneNumber] = useState<string>();
    const [error, setError] = useState<string | undefined>();

    const selectedCountryCode = useMemo(() => {
        const selectedCode = countries.all.find(({ alpha2 }) => alpha2 === countryCode);
        return selectedCode?.countryCallingCodes[0].replace(/\+/g, "") || "";
    }, [countryCode]);

    const handlePhoneNumberChange = (event: React.ChangeEvent<any>) => {
        const value = event.currentTarget.value;
        setPhoneNumber(value);
        const phone = selectedCountryCode + value;
        if (phone.length >= PHONE_MIN && phone.length <= PHONE_MAX) {
            setError(undefined);
        }
    };

    const handleCountryCodeChange = (event: React.ChangeEvent<any>) => {
        const value = event.target.value;
        setCountryCode(value);
    };

    const handlePhoneSubmit = () => {
        const phone = selectedCountryCode + phoneNumber;
        if (!phone || phone.length < PHONE_MIN || phone.length > PHONE_MAX) {
            setError(intl.formatMessage(messages.phoneNumberMaxMinLengthValidationError));
            return;
        }

        if (phoneNumber && selectedCountryCode) {
            onPhoneNumberSubmit(selectedCountryCode, String(phoneNumber));
        }
    };

    const handleKeyPress = (event: React.KeyboardEvent) => {
        if (event.key === "Enter") {
            handlePhoneSubmit();
        }
    };

    return (
        <div>
            <div className={classes.header}>
                <ViaTypography
                    variant="display.semi-strong"
                    color={viaColorTokens.text.primary.medium}
                >
                    <FormattedMessage defaultMessage="Welcome to" id="application.login.welcome" />
                </ViaTypography>
                <ViaTypography
                    variant="display.semi-strong"
                    color={viaColorTokens.via.emphasis}
                    className={classes.viacal}
                >
                    <FormattedMessage defaultMessage="ViaCal" id="application.title" />
                </ViaTypography>
            </div>
            <ViaTypography>
                <FormattedMessage
                    defaultMessage="Enter your phone number to get started"
                    id="application.login.enter-phone-to-get-started"
                />
            </ViaTypography>
            <div className={classes.fields}>
                <ViaSelect
                    width={95}
                    value={countryCode}
                    options={countriesOptions}
                    renderValue={(value) => (
                        <span>
                            (
                            {
                                countries.all.find(({ alpha2 }) => alpha2 === value)!
                                    .countryCallingCodes[0]
                            }
                            )
                        </span>
                    )}
                    onChange={handleCountryCodeChange}
                    disabled={isLoading}
                    inputDataTestId="countryCodeDataTestId"
                />
                <ViaTextfield
                    width={250}
                    placeholder={intl.formatMessage(messages.phoneNumberPlaceholder)}
                    type="number"
                    value={phoneNumber}
                    onChange={handlePhoneNumberChange}
                    showNumberButtons={false}
                    disabled={isLoading}
                    onKeyPress={handleKeyPress}
                    error={!!error}
                    dataTestId="phoneNumberDataTestId"
                />
            </div>
            {error && (
                <ViaTypography
                    className={classes.errorMessage}
                    color={viaColorTokens.feedback.negative.strong}
                >
                    {error}
                </ViaTypography>
            )}
            <ViaButton
                loading={isLoading}
                disabled={!countryCode || !phoneNumber}
                width="fullWidth"
                onClick={handlePhoneSubmit}
                dataTestId="submitDataTestId"
            >
                <FormattedMessage defaultMessage="Continue" id="application.login.btn-continue" />
            </ViaButton>
        </div>
    );
};
